import { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import useUser from "@/api/apis";
import { AuthUtils } from "@/utils/AuthUtils";
import { useTrans } from "@/utils/useTrans";
import { OpinionDialog } from "@/v2-components/pages/software/components/LeaveFeedback";

/**
 * 피드백 배너 — 시안 `30035:23462`(데스크탑) · `30035:23463`(모바일).
 *
 * ## 초판이 여기서 두 번 틀렸다
 *
 * 1. `get_metadata` 가 `Contents` 자식을 0개로 줘서 **「빈 프레임」으로 읽고** 점선
 *    자리표시를 넣었다. 스크린샷에는 배너가 있었다.
 * 2. 그래서 기존 `VrpFeedbackSection` 을 붙였는데 그건 **종전 페이지용 레이아웃**
 *    (좌측 제목 + 우측 이미지)이라 시안과 다르다. 시안은 **가운데 정렬 밴드**다.
 *
 * ## 시안 값
 *
 * ```
 * Contents  h200 · r12 · px-24 · flex-col gap-32 · items-center justify-center
 *           배경 이미지 object-cover
 * 제목      22/28 white · 가운데
 * 버튼      h40 · r44 · bg #121619(cg-100) · 12/16 SemiBold white · tracking .1 · px-24
 * ```
 *
 * 동작은 기존과 같다 — 비로그인은 로그인으로 보내고, 로그인 상태면 `OpinionDialog` 를
 * 연다. 그 로직만 `LeaveFeedback` 에서 가져왔다(다이얼로그 자체는 export 돼 있어 재사용).
 */
export default function SwFeedbackBand() {
  const { t } = useTrans(["software"]);
  const { user } = useUser();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  return (
    <>
      {open && (
        <OpinionDialog platform="VRP" onOpenChange={() => setOpen(false)} />
      )}
      <section className="flex w-full justify-center">
        <div className="mx-4 flex w-full max-w-screen-max laptop:max-w-[952px]">
          <div className="relative flex h-[200px] w-full flex-col items-center justify-center gap-8 overflow-hidden rounded-xl px-6">
            {/**
             * ⚠️ **시안 자산이 아니라 기존 이미지다**(작성자 지정).
             *
             * 시안 `30035:23463` 의 배경을 받았더니 437×655 / **1.2KB** 로 사실상 빈
             * 파일이었다(추출 실패). 대신 게임 상세 하단 피드백 배너
             * (`Inquiry.tsx` — `/games/experiences/{slug}`)가 쓰는 이미지를 그대로 쓴다.
             * 같은 성격의 배너이고 구조도 같다(가운데 정렬 · 흰 제목 · 다크 pill 버튼).
             *
             * `inquiry.png` 1904×400 · `inquiry-m.png` 656×400 — 폭별로 나뉘어 있다.
             */}
            <Image
              src="/images/games/detail/inquiry-m.png"
              alt=""
              fill
              sizes="100vw"
              className="object-cover laptop:hidden"
            />
            <Image
              src="/images/games/detail/inquiry.png"
              alt=""
              fill
              sizes="952px"
              className="hidden object-cover laptop:block"
            />
            <p className="relative text-center text-2xl text-white">
              {t("Have a feature in mind?")}
            </p>
            <button
              type="button"
              onClick={() => {
                if (!user) {
                  router.push(AuthUtils.getLoginUrl());
                  return;
                }
                setOpen(true);
              }}
              className="relative flex h-10 items-center justify-center rounded-[44px] bg-coolgray-100 px-6 text-sm font-600 tracking-[0.1px] text-white"
            >
              {t("Suggest a Feature")}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
